import { Column, SortingFn, Table } from "@tanstack/react-table";
import { Virtualizer } from "@tanstack/react-virtual";
import { toInstantOrNow } from "utils/time-utils";
import { Row } from "./DatasetDataInputTableTypes";

export const sortByDate: SortingFn<Row> = (a, b, id) => {
  const timeA = toInstantOrNow(a.getValue(id)).toEpochMilli();
  const timeB = toInstantOrNow(b.getValue(id)).toEpochMilli();
  return timeA - timeB;
};

export const handleCopy = (
  e: React.KeyboardEvent<HTMLDivElement>,
  selectedCells: Set<string>,
  rows: Row[],
  table: Table<Row>
): void => {
  e.preventDefault();

  const allCols = table.getAllColumns();
  const coords = Array.from(selectedCells)
    .map(key => key.split("-").map(Number))
    .sort((a, b) => (a[0] === b[0] ? a[1] - b[1] : a[0] - b[0]));

  const minRow = Math.min(...coords.map(([r]) => r));
  const maxRow = Math.max(...coords.map(([r]) => r));
  const minCol = Math.min(...coords.map(([, c]) => c));
  const maxCol = Math.max(...coords.map(([, c]) => c));

  const text = Array.from({ length: maxRow - minRow + 1 })
    .map((_, rOffset) => {
      const rowIdx = minRow + rOffset;
      return Array.from({ length: maxCol - minCol + 1 })
        .map((__, cOffset) => {
          const colIdx = minCol + cOffset;
          const col = allCols[colIdx];
          const val = rows[rowIdx]?.[col.id];
          return val ?? "";
        })
        .join("\t");
    })
    .join("\n");

  navigator.clipboard.writeText(text);
};

export const handleDelete = (
  e: React.KeyboardEvent<HTMLDivElement>,
  selectedCells: Set<string>,
  rows: Row[],
  table: Table<Row>,
  editingCell: { row: number; col: number } | null,
  registerUpdate: (colId: string, timestamp: string, value: string | null) => void,
  setRows: (newRows: Row[]) => void,
  pushHistory: (newRows: Row[]) => void
): void => {
  if (editingCell || !["Delete", "Backspace"].includes(e.key)) return;

  e.preventDefault();

  const newRows = [...rows];
  const allCols = table.getAllColumns();

  selectedCells.forEach(key => {
    const [r, c] = key.split("-").map(Number);
    const col = allCols[c];
    if (!col?.columnDef.meta?.editable) return;

    const colId = col.id;
    const timestamp = newRows[r]?.id;
    if (!timestamp) return;

    newRows[r][colId] = null;
    registerUpdate(colId, timestamp, null);
  });

  pushHistory(newRows);
  setRows(newRows);
};

const findNextEditableCol = (startCol: number, direction: 1 | -1, allCols: Column<Row>[]): number => {
  let next = startCol + direction;
  while (next >= 0 && next < allCols.length) {
    if (allCols[next]?.columnDef?.meta?.editable) return next;
    next += direction;
  }
  return startCol;
};

export const handleSelectionNavigation = (
  e: React.KeyboardEvent<HTMLDivElement>,
  editingCell: { row: number; col: number } | null,
  selectedCells: Set<string>,
  table: Table<Row>,
  setSelectedCells: (cells: Set<string>) => void,
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>,
  tabStartCol: number | null,
  setTabStartCol: (c: number | null) => void,
  startingCell: { row: number; col: number } | null,
  setStartingCell: (c: { row: number; col: number } | null) => void
): void => {
  if (editingCell) return;
  if (!["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight", "Tab", "Enter"].includes(e.key)) return;

  const shift = e.shiftKey;

  const coords = Array.from(selectedCells)
    .map(k => k.split("-").map(Number))
    .sort((a, b) => a[0] - b[0] || a[1] - b[1]);

  const minRow = coords[0][0];
  const maxRow = coords[coords.length - 1][0];
  const minCol = Math.min(...coords.map(c => c[1]));
  const maxCol = Math.max(...coords.map(c => c[1]));

  const maxRowIndex = table.getRowModel().rows.length - 1;
  const allCols = table.getAllColumns();

  e.preventDefault();

  if (!shift && coords.length === 1) {
    const [ar, ac] = coords[0];
    if (!startingCell || startingCell.row !== ar || startingCell.col !== ac) {
      setStartingCell({ row: ar, col: ac });
    }
  }

  if (shift) {
    const [cr, cc] = coords[0];

    if (!startingCell) {
      setStartingCell({ row: cr, col: cc });
    }
    const initialCell = startingCell ?? { row: cr, col: cc };

    const anchorRow = initialCell.row;
    const anchorCol = initialCell.col;

    const newSel = new Set(selectedCells);

    if (e.key === "ArrowDown") {
      if (minRow < anchorRow) {
        for (let col = minCol; col <= maxCol; col++) newSel.delete(`${minRow}-${col}`);
      } else {
        const newRow = Math.min(maxRow + 1, maxRowIndex);
        for (let col = minCol; col <= maxCol; col++) newSel.add(`${newRow}-${col}`);
        rowVirtualizer.scrollToIndex(newRow);
      }
      setSelectedCells(newSel);
      return;
    }

    if (e.key === "ArrowUp") {
      if (maxRow > anchorRow) {
        for (let col = minCol; col <= maxCol; col++) newSel.delete(`${maxRow}-${col}`);
      } else {
        const newRow = Math.max(minRow - 1, 0);
        for (let col = minCol; col <= maxCol; col++) newSel.add(`${newRow}-${col}`);
        rowVirtualizer.scrollToIndex(newRow);
      }
      setSelectedCells(newSel);
      return;
    }

    if (e.key === "ArrowRight") {
      if (minCol < anchorCol) {
        for (let row = minRow; row <= maxRow; row++) newSel.delete(`${row}-${minCol}`);
      } else {
        const newCol = findNextEditableCol(maxCol, 1, allCols);
        for (let row = minRow; row <= maxRow; row++) newSel.add(`${row}-${newCol}`);
      }
      setSelectedCells(newSel);
      return;
    }

    if (e.key === "ArrowLeft") {
      if (maxCol > anchorCol) {
        for (let row = minRow; row <= maxRow; row++) newSel.delete(`${row}-${maxCol}`);
      } else {
        const newCol = findNextEditableCol(minCol, -1, allCols);
        for (let row = minRow; row <= maxRow; row++) newSel.add(`${row}-${newCol}`);
      }
      setSelectedCells(newSel);
      return;
    }
  }

  if (coords.length !== 1) return;

  let [r, c] = coords[0];

  if (e.key === "ArrowDown") r = Math.min(r + 1, maxRowIndex);
  if (e.key === "ArrowUp") r = Math.max(r - 1, 0);
  if (e.key === "ArrowRight") c = findNextEditableCol(c, 1, allCols);
  if (e.key === "ArrowLeft") c = findNextEditableCol(c, -1, allCols);

  if (e.key === "Tab") {
    if (tabStartCol === null) setTabStartCol(c);
    const direction = shift ? -1 : 1;
    c = findNextEditableCol(c, direction, allCols);
  }

  if (e.key === "Enter") {
    r = shift ? r - 1 : r + 1;
    r = Math.max(0, Math.min(maxRowIndex, r));
    c = tabStartCol ?? c;
    setTabStartCol(null);
  }

  setSelectedCells(new Set([`${r}-${c}`]));
  rowVirtualizer.scrollToIndex(r);
};

export const handleEditNavigation = (
  e: React.KeyboardEvent<HTMLDivElement>,
  table: Table<Row>,
  rows: Row[],
  editingCell: { row: number; col: number } | null,
  editValue: string,
  registerUpdate: (colId: string, timestamp: string, value: string | null) => void,
  setEditingCell: (cell: { row: number; col: number } | null) => void,
  setEditValue: (value: string) => void,
  setSelectedCells: (cells: Set<string>) => void,
  pushHistory: (newRows: Row[]) => void,
  tabStartCol: number | null,
  setTabStartCol: (c: number | null) => void
): void => {
  if (!editingCell || !["Enter", "Tab"].includes(e.key)) return;

  e.preventDefault();

  const allCols = table.getAllColumns();
  const colId = allCols[editingCell.col].id;

  const updated = [...rows];
  const timestamp = updated[editingCell.row].id;
  updated[editingCell.row][colId] = editValue === "" ? null : editValue;
  pushHistory(updated);
  registerUpdate(colId, timestamp, editValue === "" ? null : editValue);
  const shift = e.shiftKey;

  if (e.key === "Tab") {
    const direction: 1 | -1 = shift ? -1 : 1;

    if (tabStartCol === null) {
      setTabStartCol(editingCell.col);
    }

    let nextCol = editingCell.col;
    nextCol += direction;
    if (nextCol < 0 || nextCol >= allCols.length) {
      nextCol = editingCell.col;
    }
    if (allCols[nextCol]?.columnDef?.meta?.editable) setEditingCell(null);
    setEditValue("");
    setSelectedCells(new Set([`${editingCell.row}-${nextCol}`]));
    return;
  }

  if (e.key === "Enter") {
    let nextRow = shift ? editingCell.row - 1 : editingCell.row + 1;

    nextRow = Math.max(0, Math.min(rows.length - 1, nextRow));

    const targetCol = tabStartCol !== null ? tabStartCol : editingCell.col;

    setTabStartCol(null);

    setEditingCell(null);
    setEditValue("");
    setSelectedCells(new Set([`${nextRow}-${targetCol}`]));
  }
};
